"use client";

import React, { useState } from "react";
import { BookOpen, CircleCheck as CheckCircle2, ExternalLink, Sparkles, TrendingUp, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const takeaways = [{
  title: "Explore 301 Prophecies",
  description: "Examine the Old Testament prophecies pointing directly to Jesus Christ as the promised Messiah."
}, {
  title: "Strengthen Your Faith Foundation",
  description: "Build unshakeable conviction through meticulous research and accessible scripture analysis."
}, {
  title: "Equip Yourself for Discussion",
  description: "Gain compelling clarity and biblical evidence to confidently share your faith with others."
}];
export default function MaximizeYourHotelsRevparToday() {
  const [isExpanded, setIsExpanded] = useState(false);
  const featureTags = [{
    label: "Faith-Centered"
  }, {
    label: "Biblically Grounded"
  }];
  return <section data-section-id="228051" id="rooted-in-purpose-hero" className="relative overflow-hidden bg-background py-10 md:py-14 lg:py-16 text-foreground">
      {/* Ambient background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -z-10 hidden md:block h-96 w-full -translate-x-1/2 max-w-7xl rounded-lg bg-accent/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-10 -z-10 hidden md:block h-72 w-72 rounded-lg bg-card/60 blur-2xl" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
          
          {/* Book Cover Card Column (Right on Desktop, First on Mobile) */}
          <div className="order-1 lg:order-2 flex justify-center lg:col-span-5">
            <div className="group relative w-full max-w-xs sm:max-w-sm lg:max-w-md">
              {/* Backing glow behind book */}
              <div aria-hidden="true" className="absolute -inset-2 rounded-lg bg-accent/20 blur-xl transition-opacity duration-500 group-hover:opacity-100" />
              
              {/* Book container */}
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-3 shadow-2xl sm:p-4">
                <AspectRatio ratio={2 / 3} className="overflow-hidden rounded-lg bg-muted/40">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/Untitled-design.png" data-wvc-srcset="https://dustinroyerbooks.com/wp-content/uploads/2026/05/Untitled2_667-x-4-in.png-wvc-srcset" data-wvc-sizes="https://dustinroyerbooks.com/wp-content/uploads/2026/05/Untitled2_667-x-4-in.png-wvc-sizes" alt="Why I Believe: The Evidence of Christ Book Cover" loading="lazy" decoding="async" className="h-full w-full object-contain object-center shadow-inner transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
                </AspectRatio>

                {/* Subtle sheen highlight banner */}
                <div className="mt-3 flex items-center justify-between px-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <BookOpen className="size-3.5 text-accent" />
                    Paperback &amp; Kindle
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-md bg-accent/20 text-accent-foreground">
                      <BookOpen className="size-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Dustin Royer</p>
                      <p className="text-[11px] text-muted-foreground">Author &amp; Bible Teacher</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Book Details & CTA Column (Left on Desktop, Second on Mobile) */}
          <div className="order-2 lg:order-1 flex flex-col items-start lg:col-span-7">
            {/* Announcement Badge */}
            <Badge variant="outline" className="mb-6 gap-2 rounded-lg border-accent/40 bg-accent/10 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-foreground uppercase">
              <TrendingUp className="size-3.5 text-accent" />
              <span>AMAZON TOP 10 NEW RELEASE</span>
            </Badge>

            {/* Editorial Serif Heading */}
            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-tight">
              Why ‘I Believe’:{" "}
              <span className="bg-gradient-to-r from-accent to-accent-foreground bg-clip-text text-accent">
                The Evidence of Christ
              </span>
            </h1>

            {/* Author Attribution */}
            <p className="mt-2 text-base font-medium tracking-wide text-muted-foreground sm:text-lg">
              By <span className="text-foreground">Dustin Royer</span>
            </p>

            {/* Subheadline / Secondary Text */}
            <p className="mt-6 text-lg text-secondary-foreground sm:text-xl">
              Biblical Prophecy, Divine Design, and Unmistakable Evidence
            </p>

            <div className={`overflow-hidden transition-[max-height] duration-300 max-w-2xl ${isExpanded ? "max-h-none" : "max-h-24 sm:max-h-28"}`}>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Discover the 301 Old Testament prophecies that point to Jesus Christ. One book. One Savior. Unmistakable evidence.
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Why ‘I Believe’: The Evidence of Christ explores the undeniable historical and biblical evidence proving Jesus is the promised Messiah. Through meticulous research and accessible scripture analysis, this book connects ancient prophecy with the life, death, and resurrection of Christ.
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Whether you are seeking answers, strengthening your personal faith, or leading a small group Bible study, this comprehensive guide offers compelling clarity, profound spiritual encouragement, and undeniable truth.
              </p>
            </div>

            <Button type="button" variant="link" onClick={() => setIsExpanded(prev => !prev)} className="mt-1 h-auto p-0 text-sm font-medium text-accent hover:underline cursor-pointer">
              {isExpanded ? "Read less" : "Read more"}
            </Button>

            {/* Feature Indicator Tags */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {featureTags.map((tag, index) => <div key={tag.label} data-index={index} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-1 text-xs font-medium text-foreground shadow-2xs">
                  <span className="size-2 rounded-lg bg-accent" />
                  <span>{tag.label}</span>
                </div>)}
              <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground pl-1">
                <Sparkles className="size-3.5 text-accent" />
                <span>Available for study groups</span>
              </div>
            </div>

            {/* Key Takeaways */}
            <Separator className="my-6 max-w-md bg-border/80" />
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                What You Will Discover Inside
              </h2>
              <div className="mt-4 space-y-3">
                {takeaways.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent">
                      <CheckCircle2 className="size-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Primary Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="group relative shadow-md transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://a.co/d/0efFVvNj" newTab className="inline-flex items-center gap-2">
                  <span>Buy on Amazon</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="group">
                <Link to="https://www.youtube.com/playlist?list=PLegS0uhpF7wzFYKMwlDIMG6A9yTYpduDo" newTab className="inline-flex items-center gap-2 font-medium">
                  <Youtube className="size-4" />
                  <span>View Study Resources</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}